import Image from 'next/image'
import { Icon } from '@captaincause/ui/Icon'
import { tm } from '@captaincause/ui/tm'
import okHand from 'assets/home-ok-hand.svg'
import { flattenImage } from 'utils/strapi/image'
import { Button } from 'components/Button'
import { StrapiImage } from 'components/StapiImage'
import CallToAction from './CallToAction'
import type { ButtonProps } from 'components/Button'
import type { LocalImage, StrapiApiImage } from 'types/strapi/image'
import type { StrapiLink } from 'types/strapi/link'

export interface ProductsProps {
  title: string | null
  subtitle: string | null
  imageBlockTitle: string | null
  listBlockTitle: string | null
  listBlockContent: string | null
  listBlockLink: (StrapiLink & { locale?: ButtonProps['locale'] }) | null
  imageBlockLink: (StrapiLink & { locale?: ButtonProps['locale'] }) | null
  image: StrapiApiImage | LocalImage
  bannerTitle: string
  bannerCTA?: (StrapiLink & { locale?: ButtonProps['locale'] }) | null
}

export default function Products({
  title,
  subtitle,
  imageBlockLink,
  imageBlockTitle,
  listBlockContent,
  listBlockLink,
  listBlockTitle,
  image,
  bannerTitle,
  bannerCTA = null,
}: ProductsProps) {
  const imageObject = 'data' in image ? flattenImage(image) : image

  if (!imageObject) return null

  const rows = listBlockContent
    ?.split('\n')
    .map(row => row.trim())
    .filter(Boolean)

  const showBanner = bannerTitle && bannerCTA

  return (
    <div className="m-auto max-w-[1400px] bg-white px-0 py-10 md:p-20">
      {title ? (
        <h2 className="mb-2 hidden text-center font-default text-[40px] font-semibold leading-[48px] -tracking-[1.5px] md:block">
          {title}
        </h2>
      ) : null}
      {subtitle ? (
        <p className="hidden text-center text-xl font-normal leading-8 text-dark-gray md:block">
          {subtitle}
        </p>
      ) : null}
      <div
        className={tm(
          'flex flex-col justify-center md:mt-10 md:gap-6 lg:flex-row',
          showBanner && 'mb-10',
        )}
      >
        <div className="flex h-auto flex-1 flex-col gap-6 border-medium-gray px-6 py-8 pt-0 md:gap-10 md:rounded-2xl md:border md:px-8 md:pt-8">
          {imageBlockTitle ? (
            <h3 className="h-auto font-default text-2xl font-semibold leading-8 -tracking-[1px] md:text-[34px] md:leading-[48px] md:-tracking-[1.5px]">
              {imageBlockTitle}
            </h3>
          ) : null}
          {'url' in imageObject ? (
            <StrapiImage
              {...imageObject}
              className="h-96 w-full rounded-2xl object-cover"
            />
          ) : (
            <Image
              src={imageObject.image}
              height={250}
              width={400}
              alt={imageObject.alt}
              className="h-auto w-full rounded-2xl object-contain sm:h-96 sm:object-cover"
            />
          )}
          {imageBlockLink?.url ? (
            <Button
              href={imageBlockLink.url}
              variant="secondary"
              className="self-center rounded-[40px] border-gray px-7 py-3 md:self-start"
              locale={imageBlockLink.locale}
            >
              {imageBlockLink.title}
            </Button>
          ) : null}
        </div>

        <div className="relative flex flex-1 flex-col justify-between gap-6 border-t-2 border-medium-gray px-6 py-8 md:gap-10 md:rounded-2xl md:border md:border-t md:px-8">
          <div className="flex flex-col gap-6 md:gap-10">
            {listBlockTitle ? (
              <h3 className="h-auto font-default text-2xl font-semibold leading-8 -tracking-[1px] md:text-[34px] md:leading-[48px] md:-tracking-[1.5px]">
                {listBlockTitle}
              </h3>
            ) : null}
            <ul className="flex max-h-96 flex-col gap-5 overflow-scroll pl-4">
              {rows?.map(content => {
                if (!content) return null

                return (
                  <li
                    className="flex list-none items-start text-sm font-normal leading-6 text-dark-gray md:text-lg md:leading-7"
                    key={content}
                  >
                    <div className="mr-3 rounded-full bg-light-purple p-1">
                      <Icon icon="check" fill="fill-dark-purple" />
                    </div>
                    {content}
                  </li>
                )
              })}
            </ul>
          </div>

          {listBlockLink?.url ? (
            <Button
              href={listBlockLink.url}
              variant="secondary"
              className="self-center rounded-[40px] border-gray px-7 py-3 md:self-start"
              locale={listBlockLink.locale}
            >
              {listBlockLink.title}
            </Button>
          ) : null}
          <Image
            src={okHand}
            alt="ok"
            className="absolute -right-16 bottom-0 -z-10 hidden lg:block"
          />
        </div>
      </div>

      {showBanner ? (
        <CallToAction
          title={bannerTitle}
          link={{ url: bannerCTA.url, title: bannerCTA.title }}
          contained
          withoutContainerPadding
          locale={bannerCTA.locale}
        />
      ) : null}
    </div>
  )
}
